// SEO JSON-LD helpers
const SITE = "https://anadoluterra.com";

// Tek kaynak kimlikler — grafikteki tüm düğümler (Service/FAQPage/WebPage/BlogPosting)
// bunlarla @id üzerinden birbirine bağlanır. 16 Eylül 2026 denetiminde bulundu:
// FAQPage ve WebSite'ın hiç @id'si yoktu, WebPage düğümü de hiç yoktu — bu yüzden
// "aynı organizasyon/site"ye ait olduklarını gösteren bağ eksikti.
export const ORGANIZATION_ID = `${SITE}/#organization`;
export const WEBSITE_ID = `${SITE}/#website`;

/**
 * Tek kaynak Organization düğümü. `postalCode` ve `sameAs` BİLEREK eksik —
 * ikisi de Levent'in netleştirmesini bekleyen açık NAP/GBP çelişkileri
 * (bkz. memory anadoluterra-nap-zombi-domainler: 2 ayrı GBP kaydı farklı posta
 * kodu taşıyor, 2 ayrı Facebook sayfası birleştirilmedi). Bunları netleşmeden
 * eklemek yanlış/çelişkili NAP verisini şemaya kalıcı olarak basar.
 */
export function organizationJsonLd(opts: { lat: number; lng: number; address: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: "Anadolu Terra A.Ş.",
    url: SITE,
    telephone: "+90-216-222-11-44",
    email: "info@anadoluterra.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: opts.address,
      addressLocality: "Ataşehir",
      addressRegion: "İstanbul",
      addressCountry: "TR",
    },
    geo: { "@type": "GeoCoordinates", latitude: opts.lat, longitude: opts.lng },
  };
}

/** Sayfa-bağımsız WebPage düğümü — Seo.tsx her sayfada otomatik ekler. */
export function webPageJsonLd(opts: {
  url: string;
  name: string;
  description: string;
  inLanguage: string;
  datePublished?: string;
  dateModified?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${opts.url}#webpage`,
    url: opts.url,
    name: opts.name,
    description: opts.description,
    inLanguage: opts.inLanguage,
    isPartOf: { "@id": WEBSITE_ID },
    about: { "@id": ORGANIZATION_ID },
    ...(opts.datePublished ? { datePublished: opts.datePublished } : {}),
    ...(opts.dateModified ? { dateModified: opts.dateModified } : {}),
  };
}

export function breadcrumbJsonLd(items: { name: string; path: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE}${it.path}`,
    })),
  };
}

export function serviceJsonLd(opts: {
  slug: string;
  name: string;
  description: string;
  serviceType?: string;
  offers?: string[];
  areaServed?: any;
  /** URL override — ilçe landing sayfaları /hizmet/:slug/:bolge altında yaşıyor */
  path?: string;
}) {
  const url = `${SITE}${opts.path || `/hizmet/${opts.slug}`}`;
  const node: any = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${url}#service`,
    name: opts.name,
    description: opts.description,
    serviceType: opts.serviceType || opts.name,
    provider: {
      "@type": "Organization",
      "@id": `${SITE}/#organization`,
      name: "Anadolu Terra A.Ş.",
      url: SITE,
    },
    areaServed: opts.areaServed || { "@type": "Country", name: "Türkiye" },
    url,
    mainEntityOfPage: { "@id": `${url}#webpage` },
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["h1", "h2", ".prose p"],
    },
  };
  if (opts.offers && opts.offers.length > 0) {
    node.hasOfferCatalog = {
      "@type": "OfferCatalog",
      name: opts.name,
      itemListElement: opts.offers.map((o, i) => ({
        "@type": "Offer",
        position: i + 1,
        itemOffered: {
          "@type": "Service",
          name: o,
          provider: { "@id": `${SITE}/#organization` },
        },
      })),
    };
  }
  return node;
}

export function itemListJsonLd(services: { slug: string; name: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Anadolu Terra Hizmetleri",
    itemListElement: services.map((s, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE}/hizmet/${s.slug}`,
      name: s.name,
    })),
  };
}

export function blogPostingJsonLd(opts: {
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  image: string;
  author: string;
}) {
  const url = `${SITE}/blog/${opts.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${url}#article`,
    headline: opts.title,
    description: opts.excerpt,
    datePublished: opts.date,
    dateModified: opts.date,
    image: opts.image.startsWith("http") ? opts.image : `${SITE}${opts.image}`,
    url,
    mainEntityOfPage: { "@id": `${url}#webpage` },
    isPartOf: { "@id": WEBSITE_ID },
    author: {
      "@type": "Organization",
      "@id": `${SITE}/#organization`,
      name: opts.author,
      url: SITE,
    },
    publisher: {
      "@type": "Organization",
      "@id": `${SITE}/#organization`,
      name: "Anadolu Terra A.Ş.",
      url: SITE,
      logo: { "@type": "ImageObject", url: `${SITE}/og-image.jpg` },
    },
  };
}

export function faqPageJsonLd(items: { question: string; answer: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    isPartOf: { "@id": WEBSITE_ID },
    about: { "@id": ORGANIZATION_ID },
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };
}
